import React, { useState, useEffect, useMemo } from 'react';
import { ArrowRight, ArrowLeft, ChevronLeft, X } from 'lucide-react';
import { 
  MapPinIcon, 
  Notification03Icon, 
  Layout01Icon, 
  Tick01Icon, 
  Loading03Icon
} from 'hugeicons-react';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useSettings } from '../../contexts/SettingsContext';
import { useLocation } from '../../contexts/LocationContext';
import Autosuggest from 'react-autosuggest';
import { Spinner } from 'flowbite-react';

const OnboardingCarousel = ({ userId, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const { updateSettings, settings } = useSettings();
  const { getLocation } = useLocation();
  const [locationError, setLocationError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDetecting, setIsDetecting] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const navigate = useNavigate();
  
  // Step-specific states
  const [locationRequested, setLocationRequested] = useState(false);
  const [locationSearch, setLocationSearch] = useState('');
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [notificationsRequested, setNotificationsRequested] = useState(false);
  const [widgetsSetup, setWidgetsSetup] = useState(false);
  const [showManualInput, setShowManualInput] = useState(false);
  
  // =====================================================================
  // ⚠️ TESTING MODE disabled for production ⚠️
  // =====================================================================
  const TESTING_MODE = false;
  
  // Determine if user is using the mobile app
  const isNativeApp = Boolean(window.isUmmahtiNative);

  // Get simplified place name from full address
  const extractPlaceName = (fullAddress) => {
    if (!fullAddress) return '';
    // Extract city/town/place from address (typically the first part before commas)
    const parts = fullAddress.split(',');
    if (parts.length > 1) {
      // Return city/town name (usually second part is more useful than first)
      return parts[1].trim();
    }
    return parts[0].trim();
  };
  
  // Check if location is already set in settings
  useEffect(() => {
    if (settings?.location && typeof settings.location === 'string') {
      setLocationRequested(true);
      setLocationSearch(settings.location);
      setSelectedLocation({
        name: settings.location
      });
    } else if (settings?.coordinates?.lat && settings?.coordinates?.lon) {
      setLocationRequested(true);
      fetchLocationName(settings.coordinates.lat, settings.coordinates.lon);
    }
  }, [settings]);

  // Fetch location name from coordinates
  const fetchLocationName = async (latitude, longitude) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`
      );
      const data = await response.json();
      if (data) {
        // Extract place name using address components
        const placeName = data.address?.city || 
                         data.address?.town || 
                         data.address?.village || 
                         data.address?.suburb ||
                         extractPlaceName(data.display_name);
        
        setLocationSearch(placeName);
        setSelectedLocation({
          name: placeName
        });
        return placeName;
      }
    } catch (error) {
      console.error("Error fetching location name:", error);
      setLocationError({message: "Failed to fetch location name. Please try again or enter manually."});
      return null;
    }
  };

  // Location search handlers
  const onSuggestionsFetchRequested = async ({ value }) => {
    if (value.length < 3) return;
    
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(value)}`
      );
      const data = await response.json();
      setLocationSuggestions(data);
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      setLocationSuggestions([]);
    }
  };

  const onSuggestionsClearRequested = () => {
    setLocationSuggestions([]);
  };

  const getSuggestionValue = (suggestion) => {
    // Return simplified place name instead of full address
    return suggestion.address?.city || 
           suggestion.address?.town || 
           suggestion.address?.village || 
           suggestion.address?.suburb ||
           extractPlaceName(suggestion.display_name);
  };

  const onSuggestionSelected = async (event, { suggestion }) => {
    const placeName = suggestion.address?.city || 
                     suggestion.address?.town || 
                     suggestion.address?.village || 
                     suggestion.address?.suburb ||
                     extractPlaceName(suggestion.display_name);
    
    setLocationSearch(placeName);
    setSelectedLocation({
      name: placeName
    });
    setLocationRequested(true);
    setLocationError(null);
    
    // Update settings with manually selected location
    await updateSettings({
      useGeolocation: false,
      location: placeName,
      coordinates: {
        lat: parseFloat(suggestion.lat),
        lon: parseFloat(suggestion.lon)
      }
    });
  };

  const handleLocationChange = (event, { newValue }) => {
    setLocationSearch(newValue);
  };

  const toggleManualInput = () => {
    setShowManualInput(true);
    setLocationError(null);
  };

  const handleLocationDetect = async () => {
    setIsDetecting(true);
    setLocationError(null);
    
    try {
      // First clear any existing location data
      setSelectedLocation(null);
      setLocationSearch('');
      
      // Request location from browser/device
      const coords = await getLocation();
      setLocationRequested(true);
      
      if (coords) {
        const { latitude, longitude } = coords;
        
        // Wait for the location name to be fetched
        const locationName = await fetchLocationName(latitude, longitude);
        
        if (locationName) {
          // Set location data
          setSelectedLocation({
            name: locationName
          });
          
          // Update settings with detected location
          await updateSettings({
            useGeolocation: true,
            location: locationName,
            coordinates: {
              lat: latitude,
              lon: longitude
            }
          });
        } else {
          throw new Error("Could not determine location name from coordinates");
        }
      } else {
        throw new Error("Location coordinates not received");
      }
    } catch (error) {
      console.error("Error detecting location:", error);
      
      // More descriptive error message for native app users
      if (isNativeApp) {
        setLocationError({
          message: "Unable to access your location. Please ensure location permissions are granted in your device settings, then try again."
        });
      } else {
        setLocationError({
          message: error.message || "Unable to detect your location. Please enter it manually."
        });
      }
    } finally {
      setIsDetecting(false);
    }
  };

  const handleLocationReset = async () => {
    setIsResetting(true);
    
    try {
      setLocationSearch('');
      setSelectedLocation(null);
      setLocationRequested(false);
      setLocationError(null);
      
      // Clear location from settings
      await updateSettings({
        useGeolocation: false,
        location: null,
        coordinates: null
      });
    } catch (error) {
      console.error("Error resetting location in settings:", error);
      setLocationError({
        message: "Failed to reset location. Please try again."
      });
    } finally {
      setIsResetting(false);
    }
  };

  // Create base steps that all users see
  const baseSteps = [
    {
      title: "Welcome to Ummahti",
      description: "Let's get you set up with a few quick steps so you can get the most out of your experience.",
      action: "Let's get started",
      renderContent: () => (
        <div className="flex flex-col items-center justify-center">
          {/* Fixed size container to prevent layout shift */}
          <div className="w-70 h-70 relative flex items-center justify-center mb-6">
            <img
              src="/images/empty_states/welcome_illustration.png"
              alt="Welcome illustration"
              className="w-full h-full object-contain animate-fade-in"
              style={{
                animation: 'fadeIn 0.6s ease-in-out'
              }}
            />
          </div>
        </div>
      )
    },
    {
      title: "Set Up Your Location",
      description: "We'll use your location to provide accurate prayer times for your area.",
      action: locationRequested ? "Continue" : "Set up location",
      customAction: async () => {
        if (!locationRequested && !selectedLocation) {
          await handleLocationDetect();
          return Boolean(selectedLocation); // Return success based on whether location was set
        }
        return true;
      },
      renderContent: () => (
        <div className="flex flex-col items-center justify-center">
          {/* Fixed size container to prevent layout shift */}
          <div className="w-40 h-40 relative flex items-center justify-center mb-6">
            <img
              src="/images/empty_states/location_empty_state.png"
              alt="Location illustration"
              className="w-full h-full object-contain animate-fade-in"
              style={{
                animation: 'fadeIn 0.6s ease-in-out'
              }}
            />
          </div>
          <h2 className="text-2xl font-serif text-white mb-3">
            Set Up Your Location
          </h2>
          <p className="text-gray-400 text-lg mb-4">
            We'll use your location to provide accurate prayer times for your area.
          </p>
          <div className="w-full max-w-md mx-auto mt-2">
            {!showManualInput ? (
              <div className="state-fade-enter">
                <button
                  onClick={handleLocationDetect}
                  disabled={isDetecting || isResetting}
                  className="flex items-end justify-center gap-2 bg-emerald-600/20 text-emerald-400 px-6 py-4 rounded-xl w-full text-base disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDetecting ? (
                    <div className="flex items-center gap-2">
                      <Spinner size="sm" />
                      <span>Detecting your location...</span>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2">
                      <MapPinIcon className="w-5 h-5" />
                      <span>Detect my location</span>
                    </div>
                  )}
                </button>
                <button 
                  onClick={toggleManualInput}
                  className="text-gray-400 text-sm hover:text-gray-300 transition-colors mt-4 block w-full text-center"
                >
                  - or input your location manually -
                </button>
              </div>
            ) : (
              <div className="state-fade-enter">
                <label className="block text-sm mb-2 text-gray-400">
                  Location
                </label>
                <Autosuggest
                  suggestions={locationSuggestions}
                  onSuggestionsFetchRequested={onSuggestionsFetchRequested}
                  onSuggestionsClearRequested={onSuggestionsClearRequested}
                  onSuggestionSelected={onSuggestionSelected}
                  getSuggestionValue={getSuggestionValue}
                  renderSuggestion={(suggestion) => {
                    const placeName = suggestion.address?.city || 
                                    suggestion.address?.town || 
                                    suggestion.address?.village || 
                                    suggestion.address?.suburb ||
                                    extractPlaceName(suggestion.display_name);
                    return (
                      <div className="text-sm py-2 px-4 hover:bg-gray-700 text-gray-300 cursor-pointer">
                        {placeName}
                      </div>
                    );
                  }}
                  inputProps={{
                    placeholder: "Enter city or town",
                    value: locationSearch,
                    onChange: handleLocationChange,
                    className: "w-full h-[42px] px-4 rounded-xl border bg-[#1A1A1A] border-gray-800 text-white focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                  }}
                  theme={{
                    container: 'relative flex-grow',
                    suggestionsContainer: 'absolute z-10 w-full mt-1 rounded-xl bg-[#1A1A1A]',
                    suggestionsContainerOpen: 'border shadow-lg border-gray-800',
                    suggestionsList: 'list-none p-0 m-0 max-h-60 overflow-auto rounded-xl',
                    suggestion: 'p-3 cursor-pointer text-gray-300',
                    suggestionHighlighted: 'bg-gray-800',
                  }}
                />
                <button 
                  onClick={() => setShowManualInput(false)}
                  className="text-gray-400 text-sm hover:text-gray-300 transition-colors mt-4 block w-full text-center"
                >
                  - or detect my location automatically -
                </button>
              </div>
            )}
            {locationError && (
              <div className="mt-4 p-3 bg-red-900/20 border border-red-500/20 rounded-xl text-red-400 text-sm">
                {locationError.message || "Unable to access your location. Please enter it manually."}
              </div>
            )}
            {selectedLocation && !locationError && (
              <div className="mt-4 p-3 bg-emerald-900/20 border border-emerald-500/20 rounded-xl text-emerald-400 text-sm">
                Location set: {selectedLocation.name}
              </div>
            )}
          </div>
        </div>
      ),
      skipDefaultText: true
    },
    {
      title: "You're All Set!",
      description: "You're ready to get the most out of Ummahti. Explore the app to discover more features.",
      action: "Start exploring",
      renderContent: () => (
        <div className="flex flex-col items-center justify-center">
          <img
            src="/images/empty_states/all_set_illustration.png"
            alt="All set illustration"
            className="w-70 h-70 mb-6"
          />
        </div>
      )
    }
  ];
  
  // App-specific steps
  const appSteps = [
    {
      title: "Enable Notifications",
      description: "Get timely reminders for prayer times throughout the day.",
      icon: <Notification03Icon className="w-10 h-10 text-emerald-400" />,
      action: "Next",
      customAction: async () => {
        if (!notificationsRequested) {
          setIsProcessing(true);
          try {
            setNotificationsRequested(true);
            setIsProcessing(false);
            return true;
          } catch (error) {
            console.error("Error with notifications:", error);
            setIsProcessing(false);
            return false;
          }
        }
        return true;
      },
      renderContent: () => (
        <div className="flex flex-col items-center justify-center">
          {/* Fixed size container to prevent layout shift */}
          <div className="w-70 h-70 relative flex items-center justify-center mb-6">
            <img
              src="/images/empty_states/onboarding_notification.png"
              alt="Notifications illustration"
              className="w-full h-full object-contain animate-fade-in"
              style={{
                animation: 'fadeIn 0.6s ease-in-out'
              }}
            />
          </div>
        </div>
      )
    },
    {
      title: "Add Widgets",
      description: "Add prayer time widgets to your home and lock screen for easy access.",
      icon: <Layout01Icon className="w-10 h-10 text-emerald-400" />,
      action: "Next",
      customAction: async () => {
        // Simply mark as viewed - this is informational for app users
        setWidgetsSetup(true);
        return true;
      },
      renderContent: () => (
        <div className="flex flex-col items-center justify-center">
          {/* Fixed size container to prevent layout shift */}
          <div className="w-70 h-70 relative flex items-center justify-center mb-6">
            <img
              src="/images/empty_states/onboarding_lockscreen.png"
              alt="Widgets illustration"
              className="w-full h-full object-contain animate-fade-in"
              style={{
                animation: 'fadeIn 0.6s ease-in-out'
              }}
            />
          </div>
        </div>
      )
    }
  ];

  // Build final steps based on whether user is in the native app or not
  const steps = useMemo(() => {
    // In testing mode, show all steps regardless of platform
    if (TESTING_MODE) {
      return [
        ...baseSteps.slice(0, 2), // Welcome and Location steps
        ...appSteps,              // App-specific steps
        baseSteps[2]              // Final "You're All Set" step
      ];
    }
    
    // In production, only show app steps for native app users
    if (isNativeApp) {
      return [
        ...baseSteps.slice(0, 2), // Welcome and Location steps
        ...appSteps,              // App-specific steps
        baseSteps[2]              // Final "You're All Set" step
      ];
    } else {
      // For web users, just show base steps
      return baseSteps;
    }
  }, [isNativeApp, TESTING_MODE, baseSteps, appSteps, locationRequested]);

  const [slideDirection, setSlideDirection] = useState('next'); // Track slide direction

  const handleNext = async () => {
    if (currentStep < steps.length - 1) {
      const currentStepObj = steps[currentStep];
      
      // If this step has a custom action, execute it
      if (currentStepObj.customAction) {
        const success = await currentStepObj.customAction();
        if (!success) return; // Don't advance if the action failed
      }
      
      setSlideDirection('next');
      setCurrentStep(currentStep + 1);
    } else {
      await completeOnboarding();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setSlideDirection('prev');
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = async () => {
    await completeOnboarding();
  };
  
  const completeOnboarding = async () => {
    setIsProcessing(true);
    
    try {
      // Mark onboarding as complete in user's profile
      if (userId) {
        try {
          const userRef = doc(db, "users", userId);
          await updateDoc(userRef, {
            onboardingCompleted: true,
            onboardingLastUpdated: new Date().toISOString()
          });
        } catch (error) {
          console.error("Error updating onboarding status:", error);
        }
      }
      
      // Save all onboarding preferences together
      await updateSettings({
        onboardingCompleted: true,
        // Only pass the name field from selectedLocation as the location value,
        // rather than the entire object
        location: selectedLocation ? selectedLocation.name : settings.location,
        useGeolocation: Boolean(locationRequested && selectedLocation)
      });
      
      // Notify parent component that onboarding is complete
      if (onComplete) {
        onComplete();
      }
    } catch (error) {
      console.error("Error completing onboarding:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Add effect to hide mobile menu using CSS
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .fixed.left-0.right-0.bottom-0.z-\\[100\\] {
        display: none !important;
      }
      .fixed.bottom-0.left-0.right-0.h-28.bg-gradient-to-t.pointer-events-none.z-\\[90\\] {
        display: none !important;
      }
      @keyframes fadeIn {
        from {
          opacity: 0;
          transform: scale(0.95);
        }
        to {
          opacity: 1;
          transform: scale(1);
        }
      }
      @keyframes slideInNext {
        from {
          opacity: 0;
          transform: translateX(30px);
        }
        to {
          opacity: 1;
          transform: translateX(0);
        }
      }
      @keyframes slideOutNext {
        from {
          opacity: 1;
          transform: translateX(0);
        }
        to {
          opacity: 0;
          transform: translateX(-30px);
        }
      }
      @keyframes slideInPrev {
        from {
          opacity: 0;
          transform: translateX(-30px);
        }
        to {
          opacity: 1;
          transform: translateX(0);
        }
      }
      @keyframes slideOutPrev {
        from {
          opacity: 1;
          transform: translateX(0);
        }
        to {
          opacity: 0;
          transform: translateX(30px);
        }
      }
      @keyframes fadeInState {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes fadeOutState {
        from { opacity: 1; }
        to { opacity: 0; }
      }
      .state-fade-enter {
        animation: fadeInState 0.2s ease-out;
      }
      .state-fade-exit {
        animation: fadeOutState 0.2s ease-out;
      }
      .step-transition-next {
        animation: slideInNext 0.4s ease-out;
      }
      .step-transition-prev {
        animation: slideInPrev 0.4s ease-out;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Function to get animation class based on slide direction
  const getStepContentClass = () => {
    return `step-transition-${slideDirection}`;
  };

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center">
      {/* Solid background to prevent transparency issues */}
      <div className="absolute inset-0 bg-[#0A0A0A] z-0"></div>
      
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Background Image Layer */}
        <img
          src="/images/headerbg.png"
          alt=""
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[1600px] max-w-none h-full object-cover opacity-10 object-top"
        />
        {/* Primary Gradient - fully solid now */}
        <div className="absolute inset-0 bg-[#0A0A0A]" />
        {/* Accent Gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-transparent via-emerald-500/[0.02] to-transparent" />
      </div>

      {/* Gradient Orbs - more subtle */}
      <div className="fixed top-1/4 left-1/4 w-[600px] h-[600px] rounded-full blur-3xl -translate-x-1/2 bg-emerald-500/[0.03]" />
      <div className="fixed bottom-1/4 right-1/4 w-[600px] h-[600px] rounded-full blur-3xl translate-x-1/2 bg-emerald-500/[0.03]" />

      {/* Content */}
      <div className="relative w-full h-full flex flex-col">
        {/* Top Bar with Back and Skip */}
        <div className="flex justify-between items-center w-full px-6 pt-8" style={{ paddingTop: 'calc(env(safe-area-inset-top) + 1rem)' }}>
          {currentStep > 0 ? (
            <button
              onClick={handlePrevious}
              disabled={isProcessing || isDetecting || isResetting}
              className="p-2 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Back"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
          ) : (
            <div className="w-6"></div>
          )}
          
          {/* Progress indicators moved to center */}
          <div className="flex justify-center space-x-2">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`h-1 rounded-full transition-all duration-300 ${
                  index === currentStep 
                    ? "w-8 bg-emerald-500" 
                    : index < currentStep 
                      ? "w-4 bg-emerald-500/70" 
                      : "w-4 bg-white/20"
                }`}
              />
            ))}
          </div>
          
          <button
            onClick={handleSkip}
            disabled={isProcessing || isDetecting || isResetting}
            className="text-gray-400 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Skip
          </button>
        </div>

        {/* Current Step Content */}
        <div className="flex-1 flex flex-col justify-center items-center px-6 overflow-hidden">
          <div className="text-center mb-8 max-w-lg mx-auto w-full relative">
            {/* Add key to force remount and animation */}
            <div key={currentStep} className={getStepContentClass()}>
              {/* Step-specific illustration/content first */}
              {steps[currentStep].renderContent && steps[currentStep].renderContent()}
              {/* Only render title/description if skipDefaultText is not true */}
              {!(steps[currentStep].skipDefaultText) && (
                <>
                  <h2 className="text-2xl font-serif text-white mb-3">
                    {steps[currentStep].title}
                  </h2>
                  <p className="text-gray-400 text-lg">
                    {steps[currentStep].description}
                  </p>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Navigation buttons - Positioned at the bottom with enough space to be above the menu */}
        <div className="p-6 pb-10" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1.5rem)' }}>
          <button
            onClick={handleNext}
            disabled={isProcessing || isDetecting || isResetting}
            className="w-full py-4 px-6 rounded-2xl backdrop-blur-md transition-all duration-300 bg-emerald-500 hover:bg-emerald-400 text-black font-medium flex items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {isProcessing ? (
              <>
                <Loading03Icon className="animate-spin w-5 h-5 mr-2" />
                <span>Processing...</span>
              </>
            ) : (
              <>
                <span>{steps[currentStep].action}</span>
                <ArrowRight className="ml-2 h-5 w-5" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingCarousel; 